import { O } from '../../../../../features/ui'
import { Address } from '../../../../../features/address'
import { Provincia } from '../../../../../services/gob-ar'
import { TaxpayerDataBody } from '../../../../../services/server'
import { VatCondition } from '../../../state/types'

export type TaxPayerLookupField = 'name' | 'vatCondition' | 'address'

export type TaxPayerLookupFieldEvaluation = {
    found: boolean
    empty: boolean
    differing: boolean
    same: boolean
    missing: boolean
}

export type TaxPayerLookupEvaluation = Record<TaxPayerLookupField, TaxPayerLookupFieldEvaluation>

export type TaxPayerLookupCurrent = {
    name?: string
    vatCondition?: VatCondition
    address?: Partial<Address>
}

const ADDRESS_PARTS = ['street', 'city', 'province'] as const

// Normaliza para comparar: sin acentos, minúsculas, sin puntuación y con espacios colapsados.
export const normalizeText = (value?: string): string =>
    (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .trim()
        .replace(/\s+/g, ' ')

const evaluateField = (found: boolean, empty: boolean, differing: boolean): TaxPayerLookupFieldEvaluation => ({
    found,
    empty,
    differing,
    same: found && !empty && !differing,
    missing: !found,
})

const currentAddressPart = (address: Partial<Address> | undefined, part: typeof ADDRESS_PARTS[number]): string | undefined => {
    if (!address) return undefined
    if (part === 'street') return address.address
    if (part === 'city') return address.locality
    return address.province
}

const evaluateAddress = (
    current: Partial<Address> | undefined,
    lookup: TaxpayerDataBody
): TaxPayerLookupFieldEvaluation => {
    const parts = ADDRESS_PARTS.filter(part => !!normalizeText(lookup.address?.[part]))
    const found = parts.length > 0
    if (!found) return evaluateField(false, false, false)

    const hasCurrentAddress = ADDRESS_PARTS.some(part => !!normalizeText(currentAddressPart(current, part)))
    const differing = hasCurrentAddress && parts.some(part =>
        normalizeText(currentAddressPart(current, part)) !== normalizeText(lookup.address?.[part])
    )

    return evaluateField(found, !hasCurrentAddress, differing)
}

export const evaluateTaxPayerLookup = (
    current: TaxPayerLookupCurrent,
    lookup: TaxpayerDataBody
): TaxPayerLookupEvaluation => {
    const lookupName = normalizeText(lookup.name)
    const currentName = normalizeText(current.name)
    const name = evaluateField(
        !!lookupName,
        !!lookupName && !currentName,
        !!lookupName && !!currentName && currentName !== lookupName
    )

    const hasLookupVatCondition = lookup.vatCondition !== undefined
    const vatCondition = evaluateField(
        hasLookupVatCondition,
        hasLookupVatCondition && current.vatCondition === undefined,
        hasLookupVatCondition && current.vatCondition !== undefined && current.vatCondition !== lookup.vatCondition
    )

    return {
        name,
        vatCondition,
        address: evaluateAddress(current.address, lookup),
    }
}

const PROVINCE_ALIASES: Record<string, string> = {
    'caba': 'Ciudad Autónoma de Buenos Aires',
    'capital federal': 'Ciudad Autónoma de Buenos Aires',
    'ciudad de buenos aires': 'Ciudad Autónoma de Buenos Aires',
    'tierra del fuego': 'Tierra del Fuego, Antártida e Islas del Atlántico Sur',
}

// Devuelve el nombre canónico de la provincia de gob.ar, o undefined para que el usuario la elija.
export const findProvinceName = (raw: string | undefined, provinces: Provincia[]): O<string> => {
    const normalized = normalizeText(raw)
    if (!normalized) return undefined
    const target = normalizeText(PROVINCE_ALIASES[normalized] ?? normalized)
    return provinces.find(province => normalizeText(province.nombre) === target)?.nombre
}
